'use client';

import React, { useEffect } from 'react';
import QuantitySelector from '@/src/app/components/QuantitySelector/QuantitySelector.tsx';
import classNames from 'classnames';
import AddToCartButton from '@/src/app/components/AddToCartButton/AddToCart.tsx';

import { Dictionary } from '@/src/lib/dictionary/types.ts';
import { DeserializedCart } from '@/src/lib/cart/glue/types/deserialized.ts';
import { CalculatePriceResponse } from '@/src/lib/product/types.ts';
import { GetUser } from '@/src/lib/auth/types.ts';
import { isProductBuyable } from '@/src/lib/product/business/product-stock.ts';
import { DeserializedCompanyUser } from '@/src/lib/company-user/types.ts';
import {
  calculateFullScalePrice,
  getFormattedPriceWithLocaleCurrency,
} from '@/src/lib/product/business/product-prices.ts';
import { CalculatePriceRequest } from '@/src/lib/product/models/calculate-price-request.model.ts';
import { useYourPriceContext } from '@/src/app/components/Providers/context/YourPriceContext.tsx';
import Loading from '@/src/app/components/LoadingSpinner/LoadingSpinner.tsx';
import { useAddToCart } from '@/src/lib/cart/hooks/use-add-to-cart.ts';
import { UserStatus } from '@/src/lib/customer/types.ts';
import { eventBus } from '@/src/lib/core/event/bus.ts';
import { EventBusName } from '@/src/lib/core/event/types.ts';
import { addToCartAction } from '@/src/lib/cart/business/add-to-cart.action.ts';
import { DeserializedConcreteProduct } from '@/src/lib/catalog-search/types.ts';
import { calculatePriceClient } from '@/src/lib/product/business/calculate-price.client.ts';

export type AddToCartFromPDPProps = {
  locale: string;
  dict: Dictionary;
  product: DeserializedConcreteProduct;
  initialCalculatePrice: CalculatePriceResponse;
  user: GetUser;
  companyUsers: Array<DeserializedCompanyUser>;
  cart?: DeserializedCart;
  className?: string;
  requestQuoteButton: React.ReactNode;
  queryYourPrice: boolean;
  accountId?: string;
  addToCart: typeof addToCartAction;
};

function AddToCartFromPDPClient({
  dict,
  locale,
  product,
  cart,
  user,
  className,
  initialCalculatePrice,
  requestQuoteButton,
  queryYourPrice,
  accountId,
  addToCart,
}: AddToCartFromPDPProps) {
  const {
    minimumOrderAmountFromProduct,
    quantity,
    setQuantity,
    onAddToCartClick,
    calculatedPrice,
    setCalculatedPrice,
    selectedPricingReference,
    isAddToCartPending,
    success,
    error,
  } = useAddToCart(product, initialCalculatePrice, addToCart, dict, locale, cart);

  const { updateYourPriceData, isLoading } = useYourPriceContext();

  async function onQuantityChange(quantity: number) {
    try {
      setQuantity(quantity);
      updateYourPriceData({ isLoading: true, yourPrice: undefined, error: null });

      const calculatePriceRequest = new CalculatePriceRequest(
        product,
        quantity,
        locale,
        user.userStatus === UserStatus.VERIFIED,
      );

      if (queryYourPrice && accountId) {
        calculatePriceRequest.setQueryYourPrice(accountId, selectedPricingReference);
      }

      const response = await calculatePriceClient(calculatePriceRequest.resultForClient);

      setQuantity(response.quantity);
      setCalculatedPrice(response);

      if (response.messages.length) {
        // eslint-disable-next-line no-console
        console.info(
          `Calculate Price Messages: %c${response.messages.join(' ')} %c`,
          'font-weight: bold;color:#fc0',
          '',
        );
      }

      if (response.yourPrice) {
        updateYourPriceData({
          isLoading: false,
          yourPrice: { ...response.yourPrice, currencyIsoCode: response.currencyIsoCode },
          error: null,
        });
      } else {
        updateYourPriceData({ isLoading: false, yourPrice: undefined, error: null });
      }
    } catch (e) {
      updateYourPriceData({ isLoading: false, yourPrice: undefined, error: e as Error });
    }
  }

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.debug('Initial Calculate Price:', initialCalculatePrice);
    // eslint-disable-next-line no-console
    console.debug('isBuyable:', isProductBuyable(product));
    // eslint-disable-next-line no-console
    console.debug('minimumOrderAmountFromProduct:', minimumOrderAmountFromProduct);

    if (queryYourPrice) {
      onQuantityChange(quantity);
    }

    function updatePriceFromEvent() {
      setQuantity(1);
      if (queryYourPrice) {
        onQuantityChange(quantity);
      }
    }

    eventBus.on(EventBusName.ACCOUNT_OR_PRICING_REFERENCE_CHANGED, updatePriceFromEvent);
    eventBus.on(EventBusName.CURRENCY_CHANGED, updatePriceFromEvent);

    return () => {
      eventBus.off(EventBusName.ACCOUNT_OR_PRICING_REFERENCE_CHANGED, updatePriceFromEvent);
      eventBus.off(EventBusName.CURRENCY_CHANGED, updatePriceFromEvent);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classNames(className, 'w-full')}>
      <div className="flex flex-col w-full">
        <div className="col-span-5 sm:col-span-4 md:col-span-5 lg:col-span-7 xl:col-span-6 2xl:col-span-7 flex flex-col text-right flex-grow sm:flex-grow-0">
          <span className="text-sm text-nowrap">{dict.product.itemTotal}</span>
          {queryYourPrice && isLoading ? (
            <Loading small className="h-6!" />
          ) : (
            <span
              itemProp="price"
              content={calculatedPrice ? calculateFullScalePrice(calculatedPrice.sumPrice).toFixed(2) : '-'}
              className="display-title-3xl font-semibold leading-7"
              data-testid="total-price"
            >
              {!calculatedPrice?.sumPrice || !calculatedPrice?.currencyIsoCode
                ? '- '
                : getFormattedPriceWithLocaleCurrency(
                    calculatedPrice.sumPrice,
                    calculatedPrice.currencyIsoCode,
                    locale.split('_')[0],
                  )}
            </span>
          )}
        </div>
        <div className="grid grid-cols-12 mt-8 gap-4">
          <div className="col-span-12 2xl:col-start-3 2xl:col-span-4 relative">
            <div className="label absolute -top-6">{dict.product.quantity}</div>
            <QuantitySelector
              quantity={quantity}
              onQuantityChange={onQuantityChange}
              className="w-full"
              disabled={isLoading}
              withDebounce={true}
              minVal={calculatedPrice.minimumOrderAmountValue || 1}
            />
            {calculatedPrice.userMessages.length ? (
              <div className="mt-2">
                <p className="text-danger text-sm">{calculatedPrice.userMessages.join(' ')}</p>
              </div>
            ) : null}
          </div>
          <div className="col-span-12 2xl:col-span-6">
            <AddToCartButton
              className="w-full"
              dict={dict}
              withLabel
              disabled={!calculatedPrice?.canBeAddedToCart || isLoading}
              isAddToCartPending={isAddToCartPending}
              onButtonClick={onAddToCartClick}
              success={success}
              error={error}
            />
          </div>

          <div className="col-span-12 2xl:col-span-6 2xl:col-start-7 flex items-stretch justify-stretch justify-items-stretch">
            {requestQuoteButton}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddToCartFromPDPClient;
